import { type ReactElement } from 'react';
import sanitizeHtml from 'sanitize-html';

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

// The AC for every PLP component title asks for two things at once: "Field type:
// single line, plain text field" AND "Support rich text". Authors therefore type
// inline HTML into an ordinary textfield and we render it here.
//
// The allowlist is what keeps those two halves compatible: only inline formatting
// survives, so no authored markup can introduce a block element and break the
// single-line layout the design depends on.
const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'span', 'sup', 'sub'];

export type Props = {
	title: string;
	headingLevel?: HeadingLevel;
	id?: string;
	className?: string;
};

export function sanitizeRichTextTitle(title: string | undefined): string {
	return sanitizeHtml(title ?? '', {
		allowedTags: ALLOWED_TAGS,
		allowedAttributes: { span: ['style'] },
	}).trim();
}

export default function RichTextTitle({
	title,
	headingLevel = 'h2',
	id,
	className,
}: Props): ReactElement | null {
	const html = sanitizeRichTextTitle(title);

	if (!html) return null;

	const Tag = headingLevel;

	return <Tag id={id} className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}
